import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Download, Plus, Search } from 'lucide-react';
import { downloadCsv } from '@/lib/utils/csv';
import { InviteUserModal } from '@/components/modals/directory-create-modals';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { UsersTable } from '@/components/settings/users-table';
import { useFirmUsersQuery } from '@/lib/api/settings';

/**
 * Settings → Users & roles (T-0110 / E01-F07).
 *
 * Required reading:
 *   - `ta-agent/output/tickets/T-0110-firm-settings-users-viewers.md`
 *   - `design_handoff_caliber_v2/prototype/screens-firm.js → firmUsers`
 *
 * Renders the firm-wide users table backed by `GET /api/v1/users` (T-0020).
 * The "Invite user" affordance carries the `data-create="user"` marker
 * called for in the ticket scope; the actual modal lives in T-0116 (and
 * the marker is the wiring contract for it). Today the button is non-
 * functional — clicking it has no effect; the marker is what T-0116 will
 * key on. This keeps the hub + table screens shippable without blocking
 * on the invite-user modal landing.
 *
 * Search is client-side filtering over the in-memory list because the
 * list endpoint doesn't yet take a `q=` filter; the page is at most a
 * few dozen users so the cost is negligible.
 */

export const Route = createFileRoute('/settings/users')({
  component: UsersScreen,
});

function UsersScreen() {
  const usersQuery = useFirmUsersQuery();
  const [inviting, setInviting] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const filtered = React.useMemo(() => {
    const all = usersQuery.data ?? [];
    if (search.trim().length === 0) return all;
    const needle = search.trim().toLowerCase();
    return all.filter(
      (u) => u.name.toLowerCase().includes(needle) || u.email.toLowerCase().includes(needle),
    );
  }, [usersQuery.data, search]);

  const total = usersQuery.data?.length ?? 0;
  const active = usersQuery.data?.filter((u) => u.status === 'active').length ?? 0;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-h1 font-semibold text-ink-900">Users &amp; roles</h1>
          <p className="text-meta text-ink-500">
            {total} user{total === 1 ? '' : 's'} · {active} active
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-500"
              aria-hidden="true"
            />
            <Input
              type="search"
              placeholder="Search users…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 w-56 pl-8 text-body-sm"
              aria-label="Search users by name or email"
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 text-meta"
            disabled={(usersQuery.data ?? []).length === 0}
            onClick={() =>
              downloadCsv(
                'firm-users.csv',
                ['name', 'email', 'role', 'status'],
                (usersQuery.data ?? []).map((u) => [u.name, u.email, u.default_role, u.status]),
              )
            }
          >
            <Download className="h-3.5 w-3.5" aria-hidden="true" />
            Export
          </Button>
          <Button
            type="button"
            onClick={() => setInviting(true)}
            variant="default"
            size="sm"
            className="h-8 gap-1.5 text-meta"
          >
            <Plus className="h-3.5 w-3.5" aria-hidden="true" />
            Invite user
          </Button>
        </div>
      </div>

      {usersQuery.isLoading ? (
        <div className="flex items-center gap-2 text-body-sm text-ink-500">
          <Spinner />
          Loading users…
        </div>
      ) : usersQuery.error ? (
        <ErrorMessage error={usersQuery.error} />
      ) : (
        <UsersTable users={filtered} />
      )}

      <InviteUserModal open={inviting} onOpenChange={setInviting} />
    </div>
  );
}
